import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/api';
import { fabric } from 'fabric';
import Loader from '../../components/loader/Loader';
import { refreshClockWidgets } from '../../designeditor/utils/refreshClockWidgets';
import './playlist-player.css';

/** Direct image file URLs — must use <img>, not iframe, or browser shows a small unstyled image */
function isDirectImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const path = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost').pathname.toLowerCase();
    return /\.(jpe?g|png|gif|webp|svg|bmp|ico|avif)$/.test(path);
  } catch {
    return /\.(jpe?g|png|gif|webp|svg|bmp|ico|avif)(\?|#|$)/i.test(url);
  }
}

let zonePlayerPromise = null;
/** Loads public/zone-player.js once (shared with web-player.html, no bundling). */
function loadZonePlayer() {
  if (typeof window !== 'undefined' && window.NotizZonePlayer) return Promise.resolve(window.NotizZonePlayer);
  if (!zonePlayerPromise) {
    zonePlayerPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${process.env.PUBLIC_URL || ''}/zone-player.js`;
      s.onload = () => resolve(window.NotizZonePlayer);
      s.onerror = () => { zonePlayerPromise = null; reject(new Error('zone-player.js failed to load')); };
      document.head.appendChild(s);
    });
  }
  return zonePlayerPromise;
}

const PlaylistPlayer = () => {
  const { id } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const timeoutRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const clockWidgetsIntervalRef = useRef(null);
  const zoneControllerRef = useRef(null);
  const destroyZones = () => {
    if (zoneControllerRef.current) {
      try { zoneControllerRef.current.destroy(); } catch (e) { /* ignore */ }
      zoneControllerRef.current = null;
    }
  };

  useEffect(() => {
    fetchPlaylist();
  }, [id]);

  useEffect(() => {
    if (playlist && playlist.content && playlist.content.length > 0) {
      playContent(0);
    }
    return () => {
      destroyZones();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (clockWidgetsIntervalRef.current) {
        clearInterval(clockWidgetsIntervalRef.current);
        clockWidgetsIntervalRef.current = null;
      }
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
      }
    };
  }, [playlist]);

  const fetchPlaylist = async () => {
    try {
      setIsLoading(true);
      // Fetch playlist by ID
      const response = await api.get(`/playlist/getplaylistbyid/${id}`);
      if (response.status === 200 && response.data.playlist) {
        setPlaylist(response.data.playlist);
      } else {
        setError('Playlist not found');
      }
    } catch (err) {
      console.error('Error fetching playlist:', err);
      setError('Failed to load playlist');
    } finally {
      setIsLoading(false);
    }
  };

  const getContentDuration = (content, index) => {
    // For videos, use the actual video duration (handled in playVideo)
    // For other content types, use assigned duration
    // Duration is stored in milliseconds in the playlist
    const duration = content.duration ?? 5000; // Default 5 seconds if no duration
    if (typeof duration === 'string') {
      const s = duration.trim();
      if (s.includes(':')) {
        const [m, sec] = s.split(':').map((x) => Number(x) || 0);
        const ms = (m * 60 + sec) * 1000;
        return ms > 0 ? ms : 5000;
      }
      const n = Number(s);
      if (!Number.isFinite(n) || n <= 0) return 5000;
      return n < 1000 ? n * 1000 : n;
    }
    if (!Number.isFinite(duration) || duration <= 0) return 5000;
    return duration < 1000 ? duration * 1000 : duration;
  };

  const isSingleImageLikeItem = (content) => {
    if (!playlist?.content || playlist.content.length !== 1 || !content) return false;
    const type = String(content.type || '').toLowerCase();
    if (type === 'image') return true;
    if (type === 'url') {
      const rawUrl = content.url || content.path || '';
      return isDirectImageUrl(rawUrl);
    }
    return false;
  };

  const playContent = (index) => {
    if (!playlist || !playlist.content || index >= playlist.content.length) {
      // Loop back to start
      if (playlist && playlist.content && playlist.content.length > 0) {
        playContent(0);
      }
      return;
    }

    const content = playlist.content[index];
    setCurrentIndex(index);
    
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Clear canvas
    destroyZones();
    const container = document.getElementById('content-container');
    if (container) {
      container.innerHTML = '';
    }

    // Handle different content types - pass index to each function
    console.log('Playing content:', { type: content.type, index, content });
    switch (content.type) {
      case 'image':
        playImage(content, index);
        break;
      case 'video':
        playVideo(content, index);
        break;
      case 'application': // PDF
        playPdf(content, index);
        break;
      case 'url':
        playUrl(content, index);
        break;
      case 'template': // Canva/FabricJS
        console.log('Playing template/canva:', content);
        playCanva(content, index);
        break;
      default:
        console.log('Unknown content type:', content.type);
        // Default: move to next after 3 seconds
        timeoutRef.current = setTimeout(() => {
          playContent(index + 1);
        }, 3000);
    }
  };
// const startfun=()=>{
//   console.log('started')
// }
  const playImage = (content, index) => {
    const container = document.getElementById('content-container');
    if (!container) return;

    container.innerHTML = '';

    const img = document.createElement('img');
    img.src = content.url || content.path;
    img.alt = '';
    img.decoding = 'async';
    img.style.objectFit = 'cover';
    img.style.objectPosition = 'center center';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.position = 'absolute';
    img.style.inset = '0';
    img.style.margin = '0';
    img.onerror = () => {
      console.error('Image load error');
      timeoutRef.current = setTimeout(() => {
        playContent(index + 1);
      }, 3000);
    };
    container.appendChild(img);

    if (isSingleImageLikeItem(content)) {
      return;
    }

    const duration = getContentDuration(content, index);
    timeoutRef.current = setTimeout(() => {
      playContent(index + 1);
    }, duration);
  };

  const playVideo = (content, index) => {
    const container = document.getElementById('content-container');
    if (!container) return;

  //  hello starting server
  // starting server
    container.innerHTML = '';

    const video = document.createElement('video');
    video.src = content.url || content.path;
    video.controls = false;
    video.autoplay = true;
    video.muted = false;
    video.playsInline = true;
    video.style.objectFit = 'cover';
    video.style.objectPosition = 'center center';
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.position = 'absolute';
    video.style.inset = '0';
    video.style.margin = '0';
    
    videoRef.current = video;

    const handleEnded = () => {
      setVideoDuration(0); // Reset for next video
      playContent(index + 1);
    };

    const handleError = (e) => {
      console.error('Video error:', e);
      setVideoDuration(0);
      // Move to next content after a short delay
      timeoutRef.current = setTimeout(() => {
        playContent(index + 1);
      }, 3000);
    };

    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    container.appendChild(video);
  };

  const playPdf = (content, index) => {
    const container = document.getElementById('content-container');
    if (!container) return;

    container.innerHTML = '';

    // Use an iframe to display PDF
    const iframe = document.createElement('iframe');
    iframe.src = `${content.url || content.path}#toolbar=0`;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.onerror = () => {
      console.error('PDF load error');
      timeoutRef.current = setTimeout(() => {
        playContent(index + 1);
      }, 3000);
    };
    container.appendChild(iframe);

    const duration = getContentDuration(content, index);
    timeoutRef.current = setTimeout(() => {
      playContent(index + 1);
    }, duration);
  };

  const playUrl = (content, index) => {
    const container = document.getElementById('content-container');
    if (!container) return;

    const rawUrl = content.url || content.path || '';
    if (isDirectImageUrl(rawUrl)) {
      playImage({ ...content, url: rawUrl, path: rawUrl }, index);
      return;
    }

    container.innerHTML = '';

    const iframe = document.createElement('iframe');
    iframe.src = content.url;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.onerror = () => {
      console.error('URL load error');
      timeoutRef.current = setTimeout(() => {
        playContent(index + 1);
      }, 3000);
    };
    container.appendChild(iframe);

    const duration = getContentDuration(content, index);
    timeoutRef.current = setTimeout(() => {
      playContent(index + 1);
    }, duration);
  };

  const playCanva = async (content, index) => {
    const container = document.getElementById('content-container');
    if (!container) return;

    try {
      console.log('playCanva called with:', { content, index, canva: content.canva });
      
      // Fetch the canva content
      let canvaData = null;
      
      // Check if canva is populated (object with canva field)
      if (content.canva && typeof content.canva === 'object' && !Array.isArray(content.canva)) {
        console.log('Canva is object:', content.canva);
        if (content.canva.canva) {
          // canva field contains the JSON string
          console.log('Found canva.canva:', typeof content.canva.canva);
          canvaData = typeof content.canva.canva === 'string' 
            ? JSON.parse(content.canva.canva) 
            : content.canva.canva;
        } else if (content.canva._id) {
          // It's a populated Content object, try to fetch it
          console.log('Canva is populated Content object, fetching...');
          try {
            const contentResponse = await api.get(`/content/getcontent/${content.canva._id}`);
            if (contentResponse.status === 200 && contentResponse.data.content) {
              const contents = Array.isArray(contentResponse.data.content)
                ? contentResponse.data.content
                : [contentResponse.data.content];
              const canvaContent = contents.find(c => c._id === content.canva._id);
              if (canvaContent && canvaContent.canva) {
                canvaData = typeof canvaContent.canva === 'string'
                  ? JSON.parse(canvaContent.canva)
                  : canvaContent.canva;
              }
            }
          } catch (err) {
            console.error('Error fetching canva content:', err);
          }
        }
      } 
      // If canva is an ObjectId string, fetch the content
      else if (content.canva && typeof content.canva === 'string') {
        if (content.canva.length === 24) {
          // Looks like ObjectId
          console.log('Canva is ObjectId string, fetching content:', content.canva);
          try {
            const contentResponse = await api.get(`/content/getcontent/${content.canva}`);
            if (contentResponse.status === 200 && contentResponse.data.content) {
              const contents = Array.isArray(contentResponse.data.content)
                ? contentResponse.data.content
                : [contentResponse.data.content];
              const canvaContent = contents.find(c => 
                c._id === content.canva || 
                c._id === content.content_id
              );
              if (canvaContent && canvaContent.canva) {
                canvaData = typeof canvaContent.canva === 'string'
                  ? JSON.parse(canvaContent.canva)
                  : canvaContent.canva;
              }
            }
          } catch (err) {
            console.error('Error fetching canva content:', err);
          }
        } else if (content.canva.startsWith('{')) {
          // JSON string
          console.log('Canva is JSON string');
          try {
            canvaData = JSON.parse(content.canva);
          } catch (e) {
            console.error('Error parsing canva JSON:', e);
          }
        }
      }
      
      // Also try using content_id if canva is not available
      if (!canvaData && content.content_id) {
        console.log('Trying to fetch using content_id:', content.content_id);
        try {
          const contentResponse = await api.get(`/content/getcontent/${content.content_id}`);
          if (contentResponse.status === 200 && contentResponse.data.content) {
            const contents = Array.isArray(contentResponse.data.content)
              ? contentResponse.data.content
              : [contentResponse.data.content];
            const canvaContent = contents.find(c => c._id === content.content_id);
            if (canvaContent && canvaContent.canva) {
              canvaData = typeof canvaContent.canva === 'string'
                ? JSON.parse(canvaContent.canva)
                : canvaContent.canva;
            }
          }
        } catch (err) {
          console.error('Error fetching canva content by content_id:', err);
        }
      }

      if (canvaData) {
        container.innerHTML = '';

        // Create a canvas element with unique ID
        const canvasId = `fabric-canvas-${index}-${Date.now()}`;
        const canvasEl = document.createElement('canvas');
        canvasEl.id = canvasId;
        canvasEl.style.width = '100%';
        canvasEl.style.height = '100%';
        canvasEl.style.display = 'block';
        container.appendChild(canvasEl);

        // Dispose previous canvas if exists
        if (clockWidgetsIntervalRef.current) {
          clearInterval(clockWidgetsIntervalRef.current);
          clockWidgetsIntervalRef.current = null;
        }
        if (fabricCanvasRef.current) {
          try {
            fabricCanvasRef.current.dispose();
          } catch (e) {
            // Ignore disposal errors
          }
        }

        // Create fabric canvas
        const canvas = new fabric.Canvas(canvasId, {
          width: canvaData.width || canvaData.canvasWidth || 1920,
          height: canvaData.height || canvaData.canvasHeight || 1080,
          backgroundColor: canvaData.backgroundColor || 'white',
        });

        fabricCanvasRef.current = canvas;

        // Load canvas from JSON
        canvas.loadFromJSON(canvaData, () => {
          // Scale canvas to fit container
          const containerWidth = container.clientWidth;
          const containerHeight = container.clientHeight;
          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;
          
          if (canvasWidth > 0 && canvasHeight > 0) {
            const scaleX = containerWidth / canvasWidth;
            const scaleY = containerHeight / canvasHeight;
            const scale = Math.min(scaleX, scaleY, 1); // Don't scale up
            
            // Update canvas dimensions
            canvas.setDimensions({
              width: canvasWidth * scale,
              height: canvasHeight * scale,
            });
            canvas.setZoom(scale);
          }
          
          canvas.renderAll();
          refreshClockWidgets(canvas);
          loadZonePlayer().then((ZP) => {
            if (fabricCanvasRef.current !== canvas || !canvas.wrapperEl) return;
            destroyZones();
            zoneControllerRef.current = ZP.mount({
              wrapperEl: canvas.wrapperEl,
              canvaJson: canvaData,
              scale: canvas.getZoom(),
              resolveUrl: (u) => u,
            });
          }).catch((e) => console.error(e));
          if (clockWidgetsIntervalRef.current) {
            clearInterval(clockWidgetsIntervalRef.current);
          }
          clockWidgetsIntervalRef.current = setInterval(() => {
            refreshClockWidgets(canvas);
          }, 100);
          // Make Login/Register elements clickable
          canvas.on('mouse:down', (e) => {
            if (e.target && e.target.linkUrl) {
              const url = e.target.linkUrl.startsWith('http') ? e.target.linkUrl : (window.location.origin + (e.target.linkUrl.startsWith('/') ? e.target.linkUrl : '/' + e.target.linkUrl));
              window.location.href = url;
            }
          });
        }, (o, object) => {
          if (!o || !object) return;
          if (o.eleType) object.eleType = o.eleType;
          if (o.registerObj) object.registerObj = o.registerObj;
          if (o.eleType === 'playlist') { object.visible = false; return; }
          if (o.clockObj !== undefined) object.clockObj = o.clockObj;
          if (object.eleType === 'login' || object.eleType === 'register') {
            object.linkUrl = o.linkUrl || (object.eleType === 'login' ? '/login' : '/register');
          }
          // Handle video objects in canvas
          if (object.eleType === 'nor-video' && object.url) {
            const videoEl = document.createElement('video');
            videoEl.src = object.url;
            videoEl.autoplay = true;
            videoEl.loop = true;
            videoEl.muted = true;
            videoEl.crossOrigin = 'anonymous';
            
            videoEl.addEventListener('loadeddata', () => {
              fabric.Image.fromURL(object.url, (img) => {
                img.set({
                  left: object.left,
                  top: object.top,
                  scaleX: object.scaleX,
                  scaleY: object.scaleY,
                  angle: object.angle,
                  eleType: 'nor-video',
                  url: object.url,
                });
                canvas.remove(object);
                canvas.add(img);
                canvas.renderAll();
              });
            });
          }
        });

        const duration = getContentDuration(content, index);
        timeoutRef.current = setTimeout(() => {
          destroyZones();
          if (clockWidgetsIntervalRef.current) {
            clearInterval(clockWidgetsIntervalRef.current);
            clockWidgetsIntervalRef.current = null;
          }
          if (fabricCanvasRef.current) {
            try {
              fabricCanvasRef.current.dispose();
            } catch (e) {
              // Ignore disposal errors
            }
            fabricCanvasRef.current = null;
          }
          playContent(index + 1);
        }, duration);
      } else {
        console.error('Canva data not found for content:', content);
        // If canva data not found, move to next
        timeoutRef.current = setTimeout(() => {
          playContent(index + 1);
        }, 3000);
      }
    } catch (err) {
      console.error('Error playing canva:', err);
      timeoutRef.current = setTimeout(() => {
        playContent(index + 1);
      }, 3000);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        width: '100vw', 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        width: '100vw', 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        flexDirection: 'column'
      }}>
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!playlist || !playlist.content || playlist.content.length === 0) {
    return (
      <div style={{ 
        width: '100vw', 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <h2>Playlist is empty</h2>
      </div>
    );
  }

  return (
    <div className="playlist-player">
      <div id="content-container" className="content-container">
        {/* Content will be dynamically inserted here */}
      </div>
    </div>
  );
};

export default PlaylistPlayer;

